import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';

import { entitiesCleanUp } from 'state/actions/stations';
import { useFormatMessage } from 'hooks';
import { getFormFieldsComponents, getCardComponent } from 'utils/schema';
import CONSTANTS from 'utils/constants';

import {
  processSatelliteAzimut,
  processSatelliteElevation,
} from 'utils/hdrainHelper';
import Satellite from 'models/Satellite';
import {
  infoSchemas,
  groupsSchemas,
  contactSchemas,
  versionSchemas,
  satelliteSchemas,
  phoneSchemas,
  measuresSchemas,
} from '../schema';

import './index.scss';

const StationForm = ({
  isEditing,
  isCreating,
  entity,
  onSubmitHandler,
  schema,
  options,
}) => {
  const {
    i18nFormScreenName,
    i18nEntityName,
    entitiesName,
    PATH_ENTITIES,
  } = CONSTANTS.ENTITIES.STATIONS;

  const [currentAllMeasures, setCurrentAllMeasures] = useState(null);
  const { userData, loading, success } = useSelector(
    (state) => ({
      userData: state.auth.userData,
      loading: state[entitiesName].loading,
      success: state[entitiesName].success,
    }),
    shallowEqual
  );

  const dispatch = useDispatch();

  const { register, handleSubmit, errors, setValue, watch } = useForm({
    defaultValues: { ...entity },
    resolver: yupResolver(schema),
  });
  const allMeasuresKey = 'allMeasures';

  const measuresFieldsToWatch = [...CONSTANTS.STATION_KEYS.MEASURES];
  const watchMeasuresFields = watch(measuresFieldsToWatch);
  const watchAllMeasuresField = watch(allMeasuresKey);

  useEffect(() => {
    return () => dispatch(entitiesCleanUp());
  }, [dispatch, success, setValue]);

  const areSignalsMeasuresEqual = () => {
    let res = false;
    const measures = measuresFieldsToWatch.map((key) => {
      return watchMeasuresFields[key];
    });
    res = measures.every((measure) => {
      return measure === measures[0];
    });
    return res;
  };

  // When user change a signal checkbox, update fields
  useEffect(() => {
    const newIsAllMeasures = watchAllMeasuresField;
    // If user has clicked on allMeasures checkbox,
    // Update measures checkboxes.
    if (
      currentAllMeasures !== null &&
      currentAllMeasures !== newIsAllMeasures
    ) {
      setCurrentAllMeasures(newIsAllMeasures);
      measuresFieldsToWatch.forEach((measure) => {
        setValue(measure, newIsAllMeasures);
      });
    } else {
      // Else the user clicked on one measure checkbox.
      // Check if all checkboxes are equal,
      // In order to update 'allMeasures' checkbox.
      const measures = measuresFieldsToWatch.map((key) => {
        return watchMeasuresFields[key];
      });
      const areAllSignalsMeasuresEqual = areSignalsMeasuresEqual();
      if (areAllSignalsMeasuresEqual && newIsAllMeasures !== measures[0]) {
        setCurrentAllMeasures(measures[0]);
        setValue(allMeasuresKey, measures[0]);
      } else if (!areAllSignalsMeasuresEqual && newIsAllMeasures) {
        // Set local state to avoir an infinite loop.
        setCurrentAllMeasures(false);
        setValue(allMeasuresKey, false);
        // Else init currentAllMeasures
      } else if (currentAllMeasures === null) {
        setCurrentAllMeasures(false);
        setValue(allMeasuresKey, false);
      }
    }
  }, [watchMeasuresFields, watchAllMeasuresField]);

  const watchSatelliteFields = watch(['satelliteId', 'location']);
  useEffect(() => {
    const { satellites = [] } = options;
    const { satelliteId = null, location = {} } = watchSatelliteFields;

    // Get latitude and longitude
    const { latitude, longitude } = location;

    let nominalOrbitalPosition = 0;
    // Get orbital position from satellite
    for (let i = 0; i < satellites.length; i += 1) {
      const satellite = Satellite(satellites[i]);

      if (satelliteId === satellite.getId() && satellite.getOrbitalPosition()) {
        nominalOrbitalPosition = satellite.getNominalOrbitalPosition();
        break;
      }
    }

    // Process azimut
    const newAzimut = processSatelliteAzimut(
      latitude,
      longitude,
      nominalOrbitalPosition
    );
    const newAzimutRounded = parseFloat(newAzimut.toFixed(2));

    // Process elevation
    const newElevation = processSatelliteElevation(
      latitude,
      longitude,
      nominalOrbitalPosition
    );
    const newElevationRounded = parseFloat(newElevation.toFixed(2));

    // Set values
    setValue('azimut', newAzimutRounded);
    setValue('elevation', newElevationRounded);
  }, [watchSatelliteFields]);
  const goBackMessage = useFormatMessage(`${i18nFormScreenName}.goBack`);

  const entityOptions = { i18nEntityName, userData, ...options };

  const cards = [
    {
      header: {
        title: useFormatMessage(`${i18nEntityName}.info`),
        icon: 'mdi mdi-information-outline',
      },
      fields: getFormFieldsComponents(
        infoSchemas,
        errors,
        register,
        isEditing ? 'edit' : 'create',
        entity,
        entityOptions
      ),
    },
    {
      header: {
        title: useFormatMessage(`${i18nEntityName}.stationsUsersGroups`),
        icon: 'mdi mdi-border-all',
      },
      fields: getFormFieldsComponents(
        groupsSchemas,
        errors,
        register,
        isEditing ? 'edit' : 'create',
        entity,
        entityOptions
      ),
      roles: [CONSTANTS.USERS_ROLES_AS_INT[90]],
    },
    {
      header: {
        title: useFormatMessage(`${i18nEntityName}.contact`),
        icon: 'mdi mdi-account-box',
      },
      fields: getFormFieldsComponents(
        contactSchemas,
        errors,
        register,
        isEditing ? 'edit' : 'create',
        entity,
        entityOptions
      ),
    },

    {
      header: {
        title: useFormatMessage(`${i18nEntityName}.versions`),
        icon: 'mdi mdi-calendar-text-outline',
      },
      fields: getFormFieldsComponents(
        versionSchemas,
        errors,
        register,
        isEditing ? 'edit' : 'create',
        entity,
        entityOptions
      ),
    },
    {
      header: {
        title: useFormatMessage(`${i18nEntityName}.satellite`),
        icon: 'mdi mdi-satellite-variant',
      },
      fields: getFormFieldsComponents(
        satelliteSchemas,
        errors,
        register,
        isEditing ? 'edit' : 'create',
        entity,
        entityOptions
      ),
    },
    {
      header: {
        title: useFormatMessage(`${i18nEntityName}.SIM`),
        icon: 'mdi mdi-phone-outline',
      },
      fields: getFormFieldsComponents(
        phoneSchemas,
        errors,
        register,
        isEditing ? 'edit' : 'create',
        entity,
        entityOptions
      ),
    },
  ];

  const allMeasuresField = (
    <div className="field is-horizontal" key={allMeasuresKey}>
      <div className="field-label is-normal">
        <label className="label" htmlFor={allMeasuresKey}>
          Select all / none
        </label>
      </div>
      <div className="field-body">
        <div className="field">
          <input
            type="checkbox"
            name={allMeasuresKey}
            id={allMeasuresKey}
            ref={register}
          />
        </div>
      </div>
    </div>
  );
  const measuresFormFieldsComponents = getFormFieldsComponents(
    measuresSchemas,
    errors,
    register,
    isEditing ? 'edit' : 'create',
    entity,
    entityOptions
  );
  const measuresCard = {
    header: {
      title: useFormatMessage(`${i18nEntityName}.measures`),
      icon: 'mdi mdi-thermometer-lines',
    },
    fields: [allMeasuresField, ...measuresFormFieldsComponents],
  };

  return (
    <>
      <div className="tile is-ancestor">
        <div className="tile is-parent">
          <div className="card tile is-child">
            <form onSubmit={handleSubmit(onSubmitHandler)}>
              {cards.map((card) => {
                const { roles } = card;
                if (
                  roles &&
                  userData &&
                  userData.role &&
                  !roles.includes(userData.role)
                ) {
                  return '';
                }
                return getCardComponent(card);
              })}

              {getCardComponent(measuresCard)}
              <hr />
              <div className="field is-horizontal">
                <div className="field-label" />
                <div className="field-body">
                  <div className="field">
                    <div className="field is-grouped">
                      <div className="control">
                        <button
                          type="submit"
                          className={`button is-primary ${
                            loading && 'is-loading'
                          }`}
                        >
                          <span>
                            {useFormatMessage(`${i18nFormScreenName}.submit`)}
                          </span>
                        </button>
                      </div>
                      {isCreating && (
                        <Link to={PATH_ENTITIES} className="button">
                          {goBackMessage}
                        </Link>
                      )}
                      {isEditing && (
                        <Link
                          to={`${PATH_ENTITIES}/${entity.id}/profile`}
                          className="button"
                        >
                          {goBackMessage}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

StationForm.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    address: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      postalCode: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.string,
    }),
    createdAt: PropTypes.string,
  }).isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  schema: PropTypes.object.isRequired,
  isCreating: PropTypes.bool,
  isEditing: PropTypes.bool,
  options: PropTypes.shape({
    satellites: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  }),
};

StationForm.defaultProps = {
  isCreating: false,
  isEditing: false,
};

export default StationForm;
